import { BaseClient } from '../base-client';
import type {
  CreateSavedReportInput,
  DrillTarget,
  ListSavedReportsQuery,
  PatchSavedReportInput,
  SavedReport,
  ShareEntryInput,
} from './types';

type ListResponse = { data: SavedReport[]; meta: { total: number; limit: number; offset: number } };
type One = { data: SavedReport };

/**
 * Typed client for saved custom reports (`/api/report-definitions/*`).
 *
 * Everything here moves DEFINITIONS. Nothing returns a result set: to see a
 * report's numbers you take its `spec` and run it through
 * `AnalyticsClient.query`, which re-scopes to whoever is asking. That is the
 * whole reason sharing is safe, and it is why there is no `run` method on this
 * client — a second way to fetch rows would be a second place for the scope to
 * be forgotten.
 *
 * `report.view` is needed for every call and `report.edit` for every write.
 * Beyond those, the rules are per ROW and the server decides them: you may edit,
 * delete, share and transfer what you OWN; you may open, run and duplicate what
 * was shared with you. `SavedReport.owned` tells the two apart.
 */
export class ReportDefinitionsClient extends BaseClient {
  /**
   * The reports this caller may run.
   *
   * `scope` partitions rather than overlaps: `mine` is ownership alone,
   * `shared` excludes what you own, and `all` is both. So a report you own and
   * also shared with everyone appears once.
   */
  list(query: ListSavedReportsQuery = {}): Promise<ListResponse> {
    return this.request('/api/report-definitions', { query: { ...query } });
  }

  /**
   * Every saved report a drill menu may consider for this caller. Which of
   * them a given click may land on is decided per click, in the browser.
   */
  drillTargets(): Promise<{ data: DrillTarget[] }> {
    return this.request('/api/report-definitions/drill-targets');
  }

  get(id: string): Promise<One> {
    return this.request(`/api/report-definitions/${encodeURIComponent(id)}`);
  }

  create(input: CreateSavedReportInput): Promise<One> {
    return this.request('/api/report-definitions', { method: 'POST', body: input });
  }

  /**
   * Edit the saved definition. OWNER ONLY, and it changes the report for
   * everybody it is shared with, immediately — there are no per-recipient
   * copies.
   */
  patch(id: string, input: PatchSavedReportInput): Promise<One> {
    return this.request(`/api/report-definitions/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: input,
    });
  }

  /** Owner only. A HARD delete: the shares go with it. */
  remove(id: string): Promise<One> {
    return this.request(`/api/report-definitions/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }

  /**
   * Take a copy, owned by whoever asked.
   *
   * The one mutation a RECIPIENT may make — it is what keeps a shared report
   * from being a dead end. Shares are not carried over: who I chose to show my
   * report to is not a property of your copy of it. Omit `name` and the server
   * picks a free one ("… (copy)").
   */
  duplicate(id: string, name?: string): Promise<One> {
    return this.request(`/api/report-definitions/${encodeURIComponent(id)}/duplicate`, {
      method: 'POST',
      body: name ? { name } : {},
    });
  }

  /** Hand the report to somebody else, shares intact. Owner only. */
  transfer(id: string, ownerUserId: string): Promise<One> {
    return this.request(`/api/report-definitions/${encodeURIComponent(id)}/transfer`, {
      method: 'POST',
      body: { ownerUserId },
    });
  }

  /**
   * Replace the WHOLE share list. Owner only, and needs `report.share`.
   *
   * A PUT of the entire list, not add/remove: send `[]` to revoke everything.
   * The server de-duplicates, so sending the same person twice tidies rather
   * than fails.
   */
  replaceShares(id: string, shares: readonly ShareEntryInput[]): Promise<One> {
    return this.request(`/api/report-definitions/${encodeURIComponent(id)}/shares`, {
      method: 'PUT',
      body: { shares },
    });
  }
}
